import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Redirect, withRouter } from 'react-router-dom';

import { AuthContext } from '../contexts/auth';
import { useMutation } from '../hooks/graph';
import { getRedirect } from '../utils/misc';
import { formControlInvalid } from '../components/forms';
import Splash from '../components/splash';
import SigninGoogle from '../components/signin-google';
import SigninApple from '../components/signin-apple';

function Signin({ history, location }) {
	const { register, handleSubmit, setError, clearErrors, formState: { errors } } = useForm();
	const { isAuthenticated, authenticate } = useContext(AuthContext);
	const [ errorState, setErrorState ] = useState(); // workaround, see below
	const [ signin, { fetching, data } ] = useMutation('signin');

	const onSubmit = values => {
		!fetching && signin(values).then(({ data: { signin: authData } }) => {

			authenticate(authData.token); // ...triggers the component refreshing

			// redirect if prompted
			if (location.search.includes('redirect=')) {
				history.push(getRedirect(location.search) || '/');
				return;
			}

			// redirect if passcode reminder (for "password" credentialType only)
			if (values.credentials?.password && !values.credentials?.passcode) {
				history.push({pathname: '/signin/passcode', state: location.state});
				return;
			}

			history.push(location.state?.from?.pathname || '/me');

		}).catch(err => {
			setError('submit', {message: err.message ||
				'We can\'t sign you in with these credentials'
			});
		});
	};

	// workaround, to make "link" work inside the condition, see below
	useEffect(() => {
		errors?.submit && setErrorState(errors?.submit);
	}, [errors?.submit]);

	return isAuthenticated ?
		<Redirect to={location.state?.from ?? '/me'} />
		:
		<Splash>
			<Splash.Body>
				<h3 className="text-center mb-3">Sign in to SignalStack</h3>

				<form className="d-grid"
					onSubmit={handleSubmit(({email, password, passcode, ...values}) =>
						onSubmit({credentials: {email, password, passcode}, ...values}))}
					onFocus={() => errors.submit && clearErrors('submit')}
				>
					<input {...register('type', {value: 'password'})} type="hidden" />

					<div>
						<label className="form-label" htmlFor="email">Email</label>
						<input {...register('email', {required: 'Required'})} type="email" id="email" className={formControlInvalid(errors.email, 'form-control')} />
						{errors.email && <span className="invalid-feedback">{errors.email.message}</span>}
					</div>

					<div className="mt-3">
						<label className="form-label" htmlFor="password">Password</label>
						<input {...register('password', {required: 'Required'})} type="password" id="password" className={formControlInvalid(errors.password, 'form-control')} />
						{errors.password && <span className="invalid-feedback">{errors.password.message}</span>}
					</div>

					<div className="mt-3">
						<label className="form-label" htmlFor="passcode">Two-factor authentication code</label>
						<input {...register('passcode')} id="passcode" placeholder="Leave blank, if not set" className={formControlInvalid(errors.passcode, 'form-control')} />
						{errors.passcode && <span className="invalid-feedback">{errors.passcode.message}</span>}
					</div>

					<button type="submit" className="btn btn-primary mt-4">Login</button>

					<div className="container my-2">
						<div className="row gx-3">
							<div className="col-2"></div>
							<div className="col-3"><div className="text-secondary"><hr/></div></div>
							<div className="col-2"><div className="text-secondary text-center py-1">or</div></div>
							<div className="col-3"><div className="text-secondary"><hr/></div></div>
							<div className="col-2"></div>
						</div>
					</div>

					<div>
						<div><SigninGoogle onSubmit={onSubmit} /></div>
						{ false && <div className="mt-2"><SigninApple onSubmit={onSubmit} /></div> }
					</div>

					{
						data && errorState && (
							<div className="alert alert-danger mt-3" role="alert">
								{errorState.message}
								<br/>
								<Link to="/auth/password/request" title="Reset password" className="">Forgot your password?</Link>
							</div>
						)
					}
				</form>
			</Splash.Body>

			{
				<div className="mt-4 text-center text-secondary">
					Forgot your password? <Link to="/auth/password/request" title="Reset password" className="">Reset&nbsp;password</Link>
				</div>
			}

			{/*
				https://nichetheory.slack.com/archives/C02DWNW5CSK/p1657726366917689
				https://nichetheory.slack.com/archives/D02EJQR576Y/p1746541531801169
			*/}
			<div className="mt-4 text-center text-secondary">
				Don&apos;t have an account? <Link to={{pathname: '/signup', state: location.state}} title="Signup" className="">Sign&nbsp;up</Link>
			</div>
		</Splash>
	;
}

export default withRouter(Signin);
